import { makeGraphqlRequestCloud } from 'api/graphql/graphQLClient';
import { InjectionReportData } from '@/graphql/cloud/injections/queries/InjectionReportData.graphql';
import { InjectionsShort } from '@/graphql/cloud/injections/queries/InjectionsShort.graphql';
import { InjectionState } from '@/graphql/cloud/injections/queries/InjectionState.graphql';
import { StartInjection } from '@/graphql/cloud/injections/mutations/StartInjection.graphql';
import { subscribe } from '@/api/graphql/wsHasuraClient';
import { RecentInjections } from '@/graphql/cloud/injections/queries/RecentInjections.graphql';
import { Injection as SubscriptionForInjection } from '@/graphql/cloud/injections/subscriptions/Injection.graphql';
import { InjectionProgress as SubscriptionForInjectionProgress } from '@/graphql/cloud/injections/subscriptions/InjectionProgress.graphql';
import { InjectionBrief as SubscriptionForInjectionBrief } from '@/graphql/cloud/injections/subscriptions/InjectionBrief.graphql';
import { transformArrayToObject } from '@/utils/objectHelpers';
import { getProgress, getProgressTimeFormatted } from '@/utils/injectionHelpers';
import { ImportChromatogramCSV } from '@/graphql/cloud/injections/mutations/ImportChromatogramCSV.graphql';
import InjectionsState from '@/graphql/cloud/injections/subscriptions/InjectionsState.graphql';
import { createGraphqlPollingSubscription } from '@/utils/subscriptionHelpers';
import { InjectionByMeasurementId } from '@/graphql/cloud/injections/queries/InjectionByMeasurementId.graphql';
import { InjectionById } from '@/graphql/cloud/injections/queries/InjectionById.graphql';
import { AddOneMinuteToInjectionRunTime } from '@/graphql/cloud/injections/mutations/AddOneMinuteToInjectionRunTime.graphql';
import { getErrorMessage } from '@/utils/errorHelpers';

export const apiInjections = {
  async getInjectionReportData(id) {
    const { injection } = await makeGraphqlRequestCloud(InjectionReportData, { id });
    if (!injection) {
      throw new Error('Injection report data was not received!');
    }
    return injection;
  },
  async getInjections(sampleId) {
    const { injections } = await makeGraphqlRequestCloud(InjectionsShort, {
      sampleId,
    });

    return {
      injections,
    };
  },
  async getInjectionState(injectionId) {
    const { injection } = await makeGraphqlRequestCloud(InjectionState, {
      injectionId,
    });

    return injection?.state;
  },
  async getInjectionById(id) {
    const { injection } = await makeGraphqlRequestCloud(InjectionById, { id });
    if (!injection) {
      throw new Error('Injection was not found.');
    }
    return injection;
  },
  subscribeForRecentInjections(deviceId, onRecentInjections, settings = {}) {
    return createGraphqlPollingSubscription({
      initialPaused: settings.isPaused ?? false,
      intervalMs: 10000,
      shouldSkip: () => !deviceId,
      fetcher: {
        document: RecentInjections,
        getVariables: () => ({ deviceId }),
      },
      onData({ injections }) {
        onRecentInjections(injections);
      },
      onError(error) {
        console.error('Error fetching recent injections:', error);
      },
    });
  },
  subscribeForInjection(injectionId, onInjection, sharedToken, sampleId) {
    return subscribe(
      SubscriptionForInjection,
      { injectionId },
      ({ injection }) => {
        onInjection({
          ...injection,
          measurements: transformArrayToObject(injection.measurements, 'id'),
          expected_duration_sec: injection.expected_duration_ms / 1000,
        });
      },
      sharedToken,
      sampleId,
    );
  },
  subscribeForInjectionProgress(injectionId, onInjectionProgress, sharedToken, sampleId) {
    return subscribe(
      SubscriptionForInjectionProgress,
      { injectionId },
      ({ injection }) => {
        onInjectionProgress({
          ...injection,
          progress: getProgress(injection.elapsed_time_ms, injection.expected_duration_ms),
          progress_time_str: getProgressTimeFormatted(
            injection.elapsed_time_ms,
            injection.expected_duration_ms,
          ),
        });
      },
      sharedToken,
      sampleId,
    );
  },
  subscribeForInjectionBrief(injectionId, onInjectionProgress) {
    return subscribe(SubscriptionForInjectionBrief, { injectionId }, ({ injection }) => {
      onInjectionProgress({
        ...injection,
        type: injection.sample.type,
        progress: getProgress(injection.elapsed_time_ms, injection.expected_duration_ms),
        progress_time_str: getProgressTimeFormatted(
          injection.elapsed_time_ms,
          injection.expected_duration_ms,
        ),
      });
    });
  },
  async startInjection(sampleId, deviceId, methodId, columnId, vial, postprocessing) {
    const { response } = await makeGraphqlRequestCloud(StartInjection, {
      input: {
        sampleId,
        deviceId,
        methodId,
        columnId,
        vial,
        postprocessing,
      },
    });

    return {
      injection: response.injection,
    };
  },
  async importChromatogramCSV({ sampleId, methodId, columnId, fileContentBase64, fileName }) {
    const { response } = await makeGraphqlRequestCloud(ImportChromatogramCSV, {
      input: {
        sampleID: sampleId,
        methodID: methodId,
        columnID: columnId,
        fileContentBase64,
        fileName,
      },
    });

    return {
      success: response?.success ?? false,
      code: response?.code ?? null,
      injection: response.injection,
    };
  },
  subscribeForInjectionsState(sampleId, onUpdate, sharedToken) {
    return subscribe(
      InjectionsState,
      { sampleId },
      ({ sample }) => {
        if (sample?.injections) {
          onUpdate(sample.injections);
        }
      },
      sharedToken,
      sampleId,
    );
  },
  async getInjectionByMeasurementId(measurementId) {
    const { measurement } = await makeGraphqlRequestCloud(InjectionByMeasurementId, {
      measurementId,
    });

    if (!measurement) {
      throw new Error('Measurement was not found.');
    }

    const injectionId = measurement.injection?.id;
    const sampleId = measurement.injection?.sample?.id;
    const sequenceId = measurement.injection?.sample?.sequence?.id;

    if (!injectionId) {
      throw new Error('Measurement is not linked to an injection.');
    }

    if (!sampleId) {
      throw new Error('Injection is not linked to a sample.');
    }

    if (!sequenceId) {
      throw new Error('Sample is not linked to a sequence.');
    }

    return {
      injectionId,
      sampleId,
      sequenceId,
    };
  },

  async addOneMinuteToInjectionRunTime(injectionId) {
    if (!injectionId) {
      throw new Error('Injection id is required.');
    }

    const { addOneMinuteToInjectionRunTime } = await makeGraphqlRequestCloud(
      AddOneMinuteToInjectionRunTime,
      {
        injectionId,
      },
    );

    if (!addOneMinuteToInjectionRunTime?.success) {
      throw new Error(
        getErrorMessage(addOneMinuteToInjectionRunTime?.code) || 'Failed to extend this injection.',
      );
    }

    const methodOverrideId = addOneMinuteToInjectionRunTime?.methodOverrideId;
    const runTimeMs = addOneMinuteToInjectionRunTime?.runTimeMs;

    return {
      methodOverrideId,
      runTimeMs,
      expectedDurationMs: addOneMinuteToInjectionRunTime?.expectedDurationMs,
      methodOverride:
        methodOverrideId != null && runTimeMs != null
          ? {
              id: methodOverrideId,
              run_time_ms: runTimeMs,
            }
          : null,
    };
  },
};
